import * as React from 'react'
import {
  ChangeEventHandler,
  FocusEventHandler,
  useRef,
  FC,
  Dispatch,
  SetStateAction,
} from 'react'
import {observer} from 'mobx-react'
import {each, keys, isInteger} from 'lodash'
import cn from 'classnames'
import {useStores} from '../../../../lib/MobXProvider'
import {WidgetApi} from '../../../../lib/WidgetApi'
import {DynamicSlotName} from '../../../../lib/DynamicSlotName'
import {
  formatAmount,
  getAmountFloatDigitsCount,
  markFieldAsNotDirty,
  parseAmount,
} from '../../../../lib/helpers'
import {WidgetEventName} from '../../../../lib/WidgetEventer'
import {ErrorMessage} from '../../../ui/ErrorMessage'
import {DynamicSlot} from '../../../dynamic/DynamicSlot'
import {InputRadioWrapper} from '../../../wrappers/InputCheckableWrapper'
import {FormPart} from '../../../wrappers/FormPart'
import {InputWrapper} from '../../../wrappers/InputWrapper'
import {RippleWrapper} from '../../../wrappers/RippleWrapper'
import {DirtyWrapper} from '../../../wrappers/DirtyWrapper'

///////////////////////////////////////////////////////////////////////////////

export type PaymentAmountsProps = {
  setCurrenciesListShown: Dispatch<SetStateAction<boolean>>
}

///////////////////////////////////////////////////////////////////////////////

export const PaymentAmounts: FC<PaymentAmountsProps> = observer((props) => {
  const customAmountRadio = useRef<HTMLInputElement>()
  const customAmountInput = useRef<HTMLInputElement>()
  const {setCurrenciesListShown} = props
  const {api} = useStores<{api: WidgetApi}>()
  const form = api.paymentForm
  const errors = form.validationErrors
  const currentAmount = form.getAmount()
  const currentCustomAmount = form.getCustomAmount()
  const availableAmounts = form.getPossibleAmounts()
  const allowCustomAmount = form.allowCustomAmount()
  const currentCurrency = form.getCurrency()
  const hasError = errors && errors.amount && form.isCustomAmount(currentAmount)
  const currencies = form.getPossibleCurrencies()
  const amountDescriptions = {}

  const showCurrencies = () => {
    setCurrenciesListShown(true)
    api.ui.focusWidgetElementBySelector(
      '.payment-currencies .currencies input[type=radio]:checked',
    )
  }

  const submit = () => form.validateAndSend()

  each(availableAmounts, (amount) => {
    const amountDescription = api.trans(
      'amount_descriptions',
      {defaultValue: null},
      {amount},
    )

    if (amountDescription) {
      amountDescriptions[amount] = amountDescription
    }
  })

  if (allowCustomAmount) {
    const amountDescription = api.trans(
      'amount_descriptions',
      {defaultValue: null},
      {amount: 'custom_amount'},
    )

    if (amountDescription) {
      amountDescriptions['custom_amount'] = amountDescription
    }
  }

  const selectAmount = async (amount: number) => {
    form.setAmount(amount, true)

    // Revert custom_amount value, that was reset on previous line
    // because we want to preserve value in custom_amount input
    // when selecting predefined amounts
    form.setCustomAmount(currentCustomAmount)

    await api.events[WidgetEventName.AMOUNT_CHANGED].publish({api})
  }

  const focusCustomAmountInput: FocusEventHandler<HTMLInputElement> = async (
    e,
  ) => {
    if (keys(form.validationErrors).indexOf('amount') === -1) {
      markFieldAsNotDirty(form.dirtyFields, 'amount')
    }

    await update(e.target.value)
  }

  const changeCustomAmountInput: ChangeEventHandler<HTMLInputElement> = async (
    e,
  ) => {
    await update(e.target.value)
  }

  const update = async (rawValue: string) => {
    // const regexp = getCustomAmountInputStrictRegExp(currentCurrency, api.computedConfig)
    //
    // if (!regexp.test(rawValue)) {
    //   return
    // }

    const value = parseAmount(rawValue, currentCurrency, api.computedConfig)

    form.setAmount(value, false)
    form.setCustomAmount(rawValue)
  }

  const blurCustomAmountInput = async () => {
    const value = parseAmount(
      currentCustomAmount,
      currentCurrency,
      api.computedConfig,
    )

    if (!value) {
      // If parsed custom_amount is null
      // (this means we could not parse number from input),
      // then clear custom_amount
      form.setCustomAmount(null)
    }

    if (value) {
      // Update amount and custom_amount
      form.setAmount(value, true)

      if (form.isPossibleAmount(value)) {
        // If parsed custom_amount is one of predefined amounts,
        // then clear custom_amount
        form.setCustomAmount(null)
      } else {
        // If parsed custom_amount is not integer,
        // then properly format it
        if (!isInteger(value)) {
          const customAmountInputFloatDigitsCount = getAmountFloatDigitsCount(
            currentCurrency,
            api.computedConfig,
          )
          const customAmount = value.toFixed(customAmountInputFloatDigitsCount)
          form.setCustomAmount(customAmount)
        }
      }
    }

    await api.events[WidgetEventName.AMOUNT_CHANGED].publish({api})
  }

  const parsedCurrentCustomAmount = parseAmount(
    currentCustomAmount,
    currentCurrency,
    api.computedConfig,
  )

  const isAmountRadioChecked = (amount: number) => {
    return currentAmount === amount && parsedCurrentCustomAmount !== amount
  }

  const isCustomAmountRadioChecked = () => {
    if (form.getPossibleAmounts().length > 0) {
      return parsedCurrentCustomAmount === currentAmount
    }

    // If no predefined amounts are available,
    // then custom_amount input checked only when amount is set
    if (form.getPossibleAmounts().length === 0) {
      return currentAmount !== null
    }
  }

  // console.log('-->', {currentAmount, currentCustomAmount, parsedCurrentCustomAmount})

  return (
    <FormPart className="payment-amounts" onSubmit={submit}>
      <DynamicSlot name={DynamicSlotName.AMOUNTS_START} form={form} />

      {currencies.length > 1 && (
        <div className="header">
          <button
            type="button"
            className="change-currency"
            onClick={showCurrencies}
          >
            <span className="link">
              {api.trans(
                'blocks.payment_amounts_and_intervals.amounts.change_currency',
              )}
            </span>
          </button>
        </div>
      )}

      <fieldset className="main">
        <legend>
          {api.trans('blocks.payment_amounts_and_intervals.amounts.legend')}
        </legend>
        <div
          className={cn('amounts', {
            'has-descriptions': keys(amountDescriptions).length > 0,
          })}
        >
          {availableAmounts.map((amount, key) => (
            <RippleWrapper key={key}>
              <label
                className={cn('label-radio-block', 'amount', {
                  selected: isAmountRadioChecked(amount),
                })}
              >
                <div className="main">
                  <InputRadioWrapper innerStyle="tick">
                    <DirtyWrapper form={form} fields="amount">
                      <input
                        type="radio"
                        aria-describedby={api.ui.generateUniqueElementId(
                          'amount-error',
                        )}
                        className="input-radio"
                        name="amount"
                        value={amount}
                        onChange={() => selectAmount(amount)}
                        checked={isAmountRadioChecked(amount)}
                      />
                    </DirtyWrapper>
                  </InputRadioWrapper>
                  <span className="code">
                    {(currentCurrency || '').toUpperCase()}
                  </span>
                  <span className="value">
                    {formatAmount(amount, currentCurrency, api.computedConfig)}
                  </span>
                </div>
                {amountDescriptions[amount] && (
                  <div className="description">
                    {amountDescriptions[amount]}
                  </div>
                )}
              </label>
            </RippleWrapper>
          ))}

          {allowCustomAmount && (
            <RippleWrapper>
              <div
                className={cn('label-radio-block', 'amount', 'custom-amount', {
                  selected: isCustomAmountRadioChecked(),
                  'has-error': hasError,
                })}
                onClick={() => customAmountInput.current.focus()}
              >
                <div className="main">
                  <span
                    aria-hidden="true"
                    style={{display: 'none'}}
                    id={api.ui.generateUniqueElementId(
                      'custom-amount-radio-label',
                    )}
                  >
                    {api.trans(
                      'blocks.payment_amounts_and_intervals.amounts.custom_amount',
                    )}
                  </span>

                  <InputRadioWrapper innerStyle="tick">
                    <input
                      type="radio"
                      id={api.ui.generateUniqueElementId('custom-amount-radio')}
                      aria-labelledby={api.ui.generateUniqueElementId(
                        'custom-amount-radio-label',
                      )}
                      className="input-radio"
                      ref={customAmountRadio}
                      name="amount"
                      onChange={() => customAmountInput.current.focus()}
                      checked={isCustomAmountRadioChecked()}
                    />
                  </InputRadioWrapper>

                  <span className="code" aria-hidden="true">
                    {(currentCurrency || '').toUpperCase()}
                  </span>
                  <span className="value">
                    <div className="form-block">
                      <label aria-hidden="true">
                        {api.trans(
                          'blocks.payment_amounts_and_intervals.amounts.custom_amount',
                        )}
                      </label>

                      <span
                        aria-hidden="true"
                        style={{display: 'none'}}
                        id={api.ui.generateUniqueElementId(
                          'custom-amount-input-label',
                        )}
                      >
                        {api.trans(
                          'blocks.payment_amounts_and_intervals.amounts.custom_amount',
                        )}
                      </span>

                      <span
                        aria-hidden="true"
                        style={{display: 'none'}}
                        id={api.ui.generateUniqueElementId(
                          'custom-amount-input-description',
                        )}
                      >
                        {api.trans(
                          'blocks.payment_amounts_and_intervals.amounts.custom_amount_description',
                          {defaultValue: ''},
                        )}
                      </span>

                      <InputWrapper form={form} fields="amount">
                        <DirtyWrapper
                          form={form}
                          fields="amount"
                          blurTimeout={200}
                          skipValidationOnBlur={!currentCustomAmount}
                        >
                          <input
                            type="text"
                            inputMode="decimal"
                            data-pattern="[0-9]*"
                            id={api.ui.generateUniqueElementId(
                              'custom-amount-input',
                            )}
                            aria-labelledby={api.ui.generateUniqueElementId(
                              'custom-amount-input-label',
                            )}
                            aria-describedby={
                              api.ui.generateUniqueElementId(
                                'custom-amount-input-description',
                              ) +
                              ' ' +
                              api.ui.generateUniqueElementId('amount-error')
                            }
                            className="input-text"
                            ref={customAmountInput}
                            value={currentCustomAmount || ''}
                            name="amount"
                            onFocus={focusCustomAmountInput}
                            onChange={changeCustomAmountInput}
                            onBlur={blurCustomAmountInput}
                            tabIndex={isCustomAmountRadioChecked() ? 0 : -1}
                          />
                        </DirtyWrapper>
                      </InputWrapper>
                    </div>
                  </span>
                </div>
                {amountDescriptions['custom_amount'] && (
                  <div className="description">
                    {amountDescriptions['custom_amount']}
                  </div>
                )}
              </div>
            </RippleWrapper>
          )}
        </div>
      </fieldset>

      <ErrorMessage
        errors={errors}
        errorKey="currency"
        id={api.ui.generateUniqueElementId('currency-error')}
      />

      <ErrorMessage
        errors={errors}
        errorKey="amount"
        id={api.ui.generateUniqueElementId('amount-error')}
      />

      <DynamicSlot name={DynamicSlotName.AMOUNTS_END} form={form} />
    </FormPart>
  )
})
